import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import dbExtended from '../utils/databaseExtended';
import { useNotification } from './NotificationContext';

// Create context
const DatabaseContext = createContext(null);

/**
 * DatabaseProvider - Context provider for database state management
 * 
 * This context provides:
 * - Connection state tracking
 * - Table list and metadata
 * - Methods for accessing and manipulating the database
 * - View synchronization between components
 */
export const DatabaseProvider = ({ children, password = null }) => {
  const notification = useNotification();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  // Database metadata
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedTableInfo, setSelectedTableInfo] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [databaseInfo, setDatabaseInfo] = useState(null);
  
  // View state
  const [activeViews, setActiveViews] = useState([]);
  const [viewState, setViewState] = useState({});
  
  // Connect to the database
  const connect = useCallback(async (pwd = password) => {
    if (isConnecting) return;
    
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      await dbExtended.getClient(pwd);
      setIsConnected(true);
      
      // Load tables after successful connection
      await refreshTables();
      
      return true;
    } catch (error) {
      setConnectionError(error.message);
      setIsConnected(false);
      notification.showError(`Database connection failed: ${error.message}`);
      
      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [isConnecting, password, notification]);
  
  // Refresh the list of tables
  const refreshTables = useCallback(async () => {
    if (!isConnected && !isConnecting) {
      return false;
    }
    
    try {
      const tablesList = await dbExtended.db.getTables();
      setTables(tablesList);
      
      // Update database info
      const stats = await dbExtended.getDatabaseStats();
      setDatabaseInfo(stats);
      
      // Update last refreshed timestamp
      setLastRefreshed(new Date());
      
      return true;
    } catch (error) {
      notification.showError(`Failed to refresh tables: ${error.message}`);
      return false;
    }
  }, [isConnected, isConnecting, notification]);
  
  // Load a table's information
  const loadTableInfo = useCallback(async (tableName) => {
    if (!tableName || (!isConnected && !isConnecting)) {
      return null;
    }
    
    try {
      const tableInfo = await dbExtended.getTableInfo(tableName);
      setSelectedTableInfo(tableInfo);
      
      return tableInfo;
    } catch (error) {
      notification.showError(`Failed to load table info: ${error.message}`);
      return null;
    }
  }, [isConnected, isConnecting, notification]);
  
  // Execute a SQL query
  const executeQuery = useCallback(async (sql, params = [], options = {}) => {
    if (!isConnected) {
      notification.showWarning('Not connected to database');
      return { success: false, error: 'Not connected to database' };
    }
    
    try {
      const result = await dbExtended.executeQuery(sql, params, options);
      
      if (!result.success) {
        notification.showError(`Query error: ${result.error}`);
      }
      
      return result;
    } catch (error) {
      notification.showError(`Query error: ${error.message}`);
      return { success: false, error: error.message };
    }
  }, [isConnected, notification]);
  
  // Get table data with filtering and pagination
  const getTableData = useCallback(async (tableName, options = {}) => {
    if (!tableName || !isConnected) {
      return null;
    }
    
    try {
      const result = await dbExtended.getTableData(tableName, options);
      
      return result;
    } catch (error) {
      notification.showError(`Failed to load table data: ${error.message}`);
      return null;
    }
  }, [isConnected, notification]);
  
  // Create a new table
  const createTable = useCallback(async (tableName, columns) => {
    if (!isConnected) {
      notification.showWarning('Not connected to database');
      return false;
    }
    
    try {
      // Build CREATE TABLE SQL
      let sql = `CREATE TABLE "${tableName}" (\n`;
      
      // Add columns
      const columnDefs = columns.map(col => {
        let def = `  "${col.name}" ${col.type}`;
        
        if (col.primaryKey) {
          def += ' PRIMARY KEY';
          
          if (col.autoIncrement && col.type === 'INTEGER') {
            def += ' AUTOINCREMENT';
          }
        }
        
        if (col.notNull || col.primaryKey) {
          def += ' NOT NULL';
        }
        
        if (col.unique && !col.primaryKey) {
          def += ' UNIQUE';
        }
        
        if (col.default) {
          def += ` DEFAULT ${col.default}`;
        }
        
        return def;
      });
      
      sql += columnDefs.join(',\n');
      sql += '\n);';
      
      // Execute the query
      const result = await executeQuery(sql);
      
      if (result.success) {
        notification.showSuccess(`Table '${tableName}' created successfully`);
        
        // Refresh tables list
        await refreshTables();
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      notification.showError(`Failed to create table: ${error.message}`);
      return false;
    }
  }, [isConnected, executeQuery, refreshTables, notification]);
  
  // Drop (delete) a table
  const dropTable = useCallback(async (tableName) => {
    if (!isConnected) {
      notification.showWarning('Not connected to database');
      return false;
    }
    
    try {
      const result = await executeQuery(`DROP TABLE IF EXISTS "${tableName}"`);
      
      if (result.success) {
        notification.showSuccess(`Table '${tableName}' deleted successfully`);
        
        // Refresh tables list
        await refreshTables();
        
        // If the deleted table was selected, clear selection
        if (selectedTable === tableName) {
          setSelectedTable(null);
          setSelectedTableInfo(null);
        }
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      notification.showError(`Failed to delete table: ${error.message}`);
      return false;
    }
  }, [isConnected, executeQuery, refreshTables, selectedTable, notification]);
  
  // Export table data
  const exportTable = useCallback(async (tableName, format = 'json', options = {}) => {
    if (!tableName || !isConnected) {
      return null;
    }
    
    try {
      const result = await dbExtended.exportTable(tableName, format, options);
      return result;
    } catch (error) {
      notification.showError(`Failed to export table: ${error.message}`);
      return null;
    }
  }, [isConnected, notification]);
  
  // Register a view in the context
  const registerView = useCallback((viewId, viewType, initialState = {}) => {
    setActiveViews(views => {
      if (!views.includes(viewId)) {
        return [...views, viewId];
      }
      return views;
    });
    
    setViewState(state => ({
      ...state,
      [viewId]: {
        type: viewType,
        ...initialState
      }
    }));
    
    return viewId;
  }, []);
  
  // Unregister a view from the context
  const unregisterView = useCallback((viewId) => {
    setActiveViews(views => views.filter(id => id !== viewId));
    
    setViewState(state => {
      const { [viewId]: _, ...rest } = state;
      return rest;
    });
  }, []);
  
  // Update a view's state
  const updateViewState = useCallback((viewId, updates) => {
    setViewState(state => ({
      ...state,
      [viewId]: {
        ...state[viewId],
        ...updates
      }
    }));
  }, []);
  
  // Get a view's state
  const getViewState = useCallback((viewId) => {
    return viewState[viewId] || null;
  }, [viewState]);
  
  // Broadcast an event to all views or specific view types
  const broadcastEvent = useCallback((eventName, data, targetViewTypes = null) => {
    // Filter views by type if targetViewTypes is specified
    const targetViews = targetViewTypes
      ? Object.entries(viewState).filter(([_, state]) => targetViewTypes.includes(state.type))
      : Object.keys(viewState);
    
    // Update each view's state with the event
    targetViews.forEach(([viewId, state]) => {
      updateViewState(viewId, {
        events: [...(state.events || []), { name: eventName, data, timestamp: Date.now() }]
      });
    });
  }, [viewState, updateViewState]);
  
  // Clear a view's events
  const clearViewEvents = useCallback((viewId) => {
    updateViewState(viewId, { events: [] });
  }, [updateViewState]);
  
  // Select a table and load its info
  const selectTable = useCallback(async (tableName) => {
    setSelectedTable(tableName);
    
    if (tableName) {
      await loadTableInfo(tableName);
      
      // Broadcast table selection to all views
      broadcastEvent('tableSelected', { tableName }, ['tableViewer', 'schemaViewer']);
    } else {
      setSelectedTableInfo(null);
    }
  }, [loadTableInfo, broadcastEvent]);
  
  // Auto-connect if password is provided
  useEffect(() => {
    if (password && !isConnected && !isConnecting && !connectionError) {
      connect(password);
    }
  }, [password, isConnected, isConnecting, connectionError, connect]);
  
  // Context value
  const value = {
    // Connection state
    isConnected,
    isConnecting,
    connectionError,
    connect,
    
    // Database metadata
    tables,
    selectedTable,
    selectedTableInfo,
    databaseInfo,
    lastRefreshed,
    
    // Database operations
    refreshTables,
    loadTableInfo,
    executeQuery,
    getTableData,
    createTable,
    dropTable,
    exportTable,
    selectTable,
    
    // View management
    registerView,
    unregisterView,
    updateViewState,
    getViewState,
    activeViews,
    broadcastEvent,
    clearViewEvents
  };
  
  return (
    <DatabaseContext.Provider value={value}>
      {children}
    </DatabaseContext.Provider>
  );
};

/**
 * useDatabase - Hook to access database context
 */
export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  
  if (!context) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  
  return context;
};

export default DatabaseContext;